import './WorldMap.css';

import axios from 'axios';
import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useNavigate} from 'react-router-dom';
import {Legend, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';

import {FetchNumberOfFoodPosts, FetchNumberOfFoodPostsPerContinent, FetchRandomFood} from './Food/FoodRequests';
import {FetchNumberOfSightseeingPosts, FetchNumberOfSightseeingPostsPerContinent, FetchRandomSightseeing, SightseeingPost} from './Sightseeing/SightseeingRequests';
import {CountrySummary, MaterialIcon} from './Utils/CountryUtility';
import {
  navigateToFoodWithCursor as navigateToFoodWithCursorHelper,
  navigateToSightseeingWithCursor as navigateToSightseeingWithCursorHelper,
} from './Utils/TopicCursorNavigation';
import InteractiveMap from './Utils/WorldMapUtility';
import {FoodPost} from './Food/FoodRequests';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const compactViewportQuery = '(max-width: 600px), ((max-height: 500px) and (orientation: landscape)), ((max-width: 900px) and (pointer: coarse))';
const CONTINENT_KEYS = ['Europe', 'Africa', 'Asia', 'North America', 'South America', 'Oceania', 'Antarctica'];
type ContinentCounts = Record<string, number>;
type PreviewItem = { id: number | string; iso: string };
type OverlayCountry = CountrySummary & { capital?: string };

interface PieSegment {
  name: string;
  key?: string;
  fill: string;
  getValue?: (counts: ContinentCounts) => number;
}

const emptyContinentCounts: ContinentCounts = Object.fromEntries(CONTINENT_KEYS.map((continent) => [continent, 0]));
const PIE_SEGMENTS = [
  {name: 'Europe', key: 'Europe', fill: '#ffc658'},
  {name: 'Africa', key: 'Africa', fill: '#ff8c42'},
  {name: 'Asia', key: 'Asia', fill: '#ff4c4c'},
  {name: 'North America', key: 'North America', fill: '#a4de6c'},
  {name: 'South America', key: 'South America', fill: 'var(--accent-color)'},
  {
    name: 'Oceania & Antarctica',
    fill: '#8dd1e1',
    getValue: (counts: ContinentCounts) => (counts['Oceania'] || 0) + (counts['Antarctica'] || 0),
  },
] as PieSegment[];

const hasValidContinentObject = (value: unknown): value is ContinentCounts => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const normalizeContinentCounts = (value: unknown): ContinentCounts => hasValidContinentObject(value) ? value : emptyContinentCounts;
const buildPieData = (counts: ContinentCounts) => PIE_SEGMENTS.map((segment) => ({
  name: segment.name,
  value: segment.getValue ? segment.getValue(counts) : (counts[segment.key || ''] || 0),
  fill: segment.fill,
}));

function isCompactViewport() {
  return window.matchMedia(compactViewportQuery).matches;
}

function WorldMap() {
  const navigate = useNavigate();
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [geoJson, setGeoJson] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<OverlayCountry | null>(null);
  const [selectedIso, setSelectedIso] = useState<string | null>(null);

  const [GetModalOpen, setIsModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] =
      useState('');
  const [searchResults, setSearchResults] =
      useState<CountrySummary[]>([]);

    const [flagUrl, setFlagUrl] = useState<string | null>(null);

  const [countryNumSightseeingPosts, setCountryNumSightseeingPosts] =
      useState<number | null>(null);
  const [totalNumSightseeingPosts, setTotalNumSightseeingPosts] =
      useState<number | null>(null);
  const [continentNumSightseeingPosts, setContinetNumSightseeingPosts] =
      useState<ContinentCounts | null>(null);

  const [countryNumFoodPosts, setCountryNumFoodPosts] =
      useState<number | null>(null);
  const [totalNumFoodPosts, setTotalNumFoodPosts] =
      useState<number | null>(null);
  const [continentNumFoodPosts, setContinetNumFoodPosts] =
      useState<ContinentCounts | null>(null);

  const [sightseeingData, setSightseeingData] =
      useState<SightseeingPost[]>([]);
    const [foodData, setFoodData] =
      useState<FoodPost[]>([]);
  const [isSmallScreen, setIsSmallScreen] = useState(isCompactViewport());
  const [isMobileCountersOpen, setIsMobileCountersOpen] = useState(false);

  const safeSightseeingContinentCounts = normalizeContinentCounts(
      continentNumSightseeingPosts);

  const pieSightseeingData = buildPieData(safeSightseeingContinentCounts);

  const safeFoodContinentCounts = normalizeContinentCounts(continentNumFoodPosts);

  const pieFoodData = buildPieData(safeFoodContinentCounts);

  const legendFormatter = (value: string): string => {
    if (value === 'North America') return 'N. America';
    if (value === 'South America') return 'S. America';
    if (value === 'Oceania & Antarctica') return 'Oceania+Ant.';
    return value;
  };

  const navigateToSightseeingWithCursor = async (item: PreviewItem) => {
    await navigateToSightseeingWithCursorHelper({
      item,
      navigate,
    });
  };

  const navigateToFoodWithCursor = async (item: PreviewItem) => {
    await navigateToFoodWithCursorHelper({
      item,
      navigate,
    });
  };

  const fetchContinentCounts = (
      fetcher: () => Promise<unknown>,
      setter: React.Dispatch<React.SetStateAction<ContinentCounts | null>>,
      errorMessage: string,
  ) => {
    fetcher()
        .then((numPosts) => {
          setter(normalizeContinentCounts(numPosts));
        })
        .catch((error) => {
          console.error(errorMessage, error);
          setter(emptyContinentCounts);
        });
  };

  const fetchTotalCount = (
      fetcher: () => Promise<number>,
      setter: React.Dispatch<React.SetStateAction<number | null>>,
      errorMessage: string,
  ) => {
    fetcher()
        .then((numPosts) => {
          setter(numPosts);
        })
        .catch((error) => {
          console.error(errorMessage, error);
          setter(null);
        });
  };

  useEffect(() => {
    const onResize = () => {
      setIsSmallScreen(isCompactViewport());
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!isSmallScreen) {
      setIsMobileCountersOpen(false);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    fetch('/assets/maps/world-50m.json')
        .then((response) => response.json())
        .then((data) => {
          setGeoJson(data);
          setIsMapLoading(false);
        })
        .catch((error) => {
          console.error('Error loading GeoJSON:', error);
        });
  }, []);

  useEffect(() => {
    async function fetchPreviewData(iso: string | null) {
      try {
        const isoFilter = iso ?? undefined;
        const [sightseeingPreviewData, foodPreviewData] = await Promise.all([
          FetchRandomSightseeing(2, 'WorldMapPage', isoFilter, undefined),
          FetchRandomFood(2, 'WorldMapPage', isoFilter, undefined),
        ]);
        setSightseeingData(sightseeingPreviewData);
        setFoodData(foodPreviewData);
      } catch (error) {
        console.error('Error fetching world map preview data:', error);
      }
    }

    fetchPreviewData(selectedIso);

    const intervalId = setInterval(() => {
      fetchPreviewData(selectedIso);
    }, 600000);

    return () => clearInterval(intervalId);
  }, [selectedIso]);

  useEffect(() => {
    fetchTotalCount(
        FetchNumberOfSightseeingPosts,
        setTotalNumSightseeingPosts,
        'Error fetching sightseeing posts:');
  }, []);

  useEffect(() => {
    fetchContinentCounts(
        FetchNumberOfSightseeingPostsPerContinent,
        setContinetNumSightseeingPosts,
        'Error fetching sightseeing posts:');
  }, []);

  useEffect(() => {
    fetchTotalCount(
        FetchNumberOfFoodPosts,
        setTotalNumFoodPosts,
        'Error fetching food posts:');
  }, []);

  useEffect(() => {
    fetchContinentCounts(
        FetchNumberOfFoodPostsPerContinent,
        setContinetNumFoodPosts,
        'Error fetching food posts:');
  }, []);

  const handleSearchClick = (countryName: string) => {
    axios.get(`${backendUrl}/worlddata/countries/${countryName}`)
        .then((response) => {
          setSelectedCountry(response.data);
          setSelectedIso(response.data.iso);
          const countryIso = response.data.iso.toLowerCase();
          setFlagUrl(`https://flagcdn.com/w320/${countryIso}.png`);
          setIsModalOpen(true);

          setSearchQuery('');
        })
        .catch(
            (error) => console.error(
                'Error fetching country data from search', error));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      axios.get(`${backendUrl}/worlddata/countries?name=${query}`)
          .then((response) => {
            setSearchResults(response.data);
          })
          .catch((error) => console.error('Error searching countries', error));
    } else {
      setSearchResults([]);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCountry(null);
    setFlagUrl(null);
    setCountryNumSightseeingPosts(null);
    setCountryNumFoodPosts(null);
  };
  const isMobile = isSmallScreen;
  return (
    <div className='WorldMapPage'>
      <Helmet>
        <title>Interactive World Map by Country | Living Out Of A Backpack</title>
        <link rel="canonical" href={`https://livingoutofabackpack.com/worldmap/`} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content="Use our interactive world map to explore countries, compare travel topics, and jump directly into sightseeing, food, and statistics pages." />
        <meta property="og:title" content="Interactive World Map by Country | Living Out Of A Backpack" />
        <meta property="og:description" content="Browse countries on an interactive map and open travel topic pages for food, sightseeing, and statistics." />
        <meta property="og:url" content="https://livingoutofabackpack.com/worldmap/" />
      </Helmet>
      <div className="MapContainer">
        <div className="SearchBarContainer">
          <div
            style={{
              position: 'relative',
              width: '100%',
              background: 'white',
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              zIndex: '1000',
            }}
          >
            <input className="SearchBarInput" type="text" placeholder="Search for a country" value={searchQuery} onChange={handleSearchChange} />
            {searchQuery.length > 0 && (
              <div className="SearchBarResults">
                <ul className="SearchBarResultsList">
                  {searchResults.length > 0 ? (
                    searchResults.map((country) => (
                      <li className="SearchBarResultsItem" key={country.iso} onClick={() => handleSearchClick(country.name)}>
                        {country.name}
                      </li>
                    ))
                  ) : (
                    <li style={{ textAlign: 'center', padding: '8px' }}>No matching countries found</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        <InteractiveMap
          isMapLoading={isMapLoading}
          isMobile={isMobile}
          geoJson={geoJson}
          setSelectedIso={setSelectedIso}
          setSelectedCountry={setSelectedCountry}
          setIsModalOpen={setIsModalOpen}
          setFlagUrl={setFlagUrl}
          setCountryNumSightseeingPosts={setCountryNumSightseeingPosts}
          setCountryNumFoodPosts={setCountryNumFoodPosts}
        />

        {GetModalOpen && selectedCountry && (
          <div className="ModalOverlay" onClick={closeModal}>
            <button
              className="OverlayCloseButton"
              onClick={closeModal}
              aria-label="Close country details"
            >
              &times;
            </button>
            <div
              className="MainOverlay"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="OverlayInfo">
                <div className="OverlayTitle">
                  <h2>{selectedCountry.name}</h2>
                </div>
                <p>
                  <strong>Capital:</strong> {selectedCountry.capital}
                </p>
                <p>
                  <strong>ISO:</strong> {selectedCountry.iso}
                </p>

                {flagUrl && (
                  <div className="OverlayFlagWrapper">
                    <img
                      className="OverlayFlag"
                      src={flagUrl}
                      alt={`${selectedCountry.name} Flag`}
                    />
                  </div>
                )}

                {(Array.isArray(sightseeingData) && sightseeingData.length > 0) ||
                (Array.isArray(foodData) && foodData.length > 0) ? (
                  <div className="OverlayPreviewRow">
                    {Array.isArray(sightseeingData) && sightseeingData.length > 0 && (
                      <div
                        className="fade-container"
                        style={{ '--slider-width': `${150}px`, '--slider-height': `${150}px`, '--slide-count': sightseeingData.length, '--duration': '3s' } as React.CSSProperties}
                        onClick={() => navigateToSightseeingWithCursor(sightseeingData[0])}
                      >
                        {sightseeingData.map((item, index) => (
                          <div
                            key={index}
                            className="fade-slide"
                            style={{ '--i': index } as React.CSSProperties}
                            onClick={(event) => {
                              event.stopPropagation();
                              navigateToSightseeingWithCursor(item);
                            }}
                          >
                            <div className="LocationLabel">
                              <div className="LocationText">{item.region}</div>
                              <MaterialIcon iconName="location_on" />
                            </div>
                            <img src={`${
    backendUrl}/django-dynamic/pictures/${
                item.images[0]}`} alt={item.place || item.activity || item.region || 'Sightseeing preview'} />
                          </div>
                        ))}
                      </div>
                    )}

                    {Array.isArray(foodData) && foodData.length > 0 && (
                      <div
                        className="fade-container"
                        style={{ '--slider-width': `${150}px`, '--slider-height': `${150}px`, '--slide-count': foodData.length, '--duration': '3s' } as React.CSSProperties}
                        onClick={() => navigateToFoodWithCursor(foodData[0])}
                      >
                        {foodData.map((item, index) => (
                          <div
                            key={index}
                            className="fade-slide"
                            style={{ '--i': index } as React.CSSProperties}
                            onClick={(event) => {
                              event.stopPropagation();
                              navigateToFoodWithCursor(item);
                            }}
                          >
                            <div className="LocationLabel">
                              <div className="LocationText">{item.dish || item.restaurant || item.region}</div>
                              <MaterialIcon iconName="restaurant_menu" />
                            </div>
                            <img src={`${
    backendUrl}/django-dynamic/pictures/${
    item.images[0]}`} alt={item.dish || item.restaurant || item.region || 'Food preview'} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="OverlayMenu">
                <div className="OverlaySightseeingTopic">
                  <li className="ButtonTopicSightseeing" onClick={() => navigate(`/traveltopics/${
    selectedCountry.name}/topics/sightseeing`)}>
                    <span>{countryNumSightseeingPosts !== null ? countryNumSightseeingPosts : 'Loading...'} </span>
                    <span>Travel Spots</span>
                    <MaterialIcon iconName="hiking" />
                  </li>
                </div>

                <div className="OverlayFoodTopic">
                  <li className="ButtonTopicFood" onClick={() => navigate(`/traveltopics/${
    selectedCountry.name}/topics/food`)}>
                    <span>{countryNumFoodPosts !== null ? countryNumFoodPosts : 'Loading...'}</span>
                    <span>Food Posts</span>
                    <MaterialIcon iconName="restaurant_menu" />
                  </li>
                </div>

                <div className="OverlayStatisticsTopic">
                  <li className="ButtonTopicStatistics" onClick={() => navigate(`/traveltopics/${
    selectedCountry.name}/topics/statistics`)}>
                    <span>Statistics</span>
                    <MaterialIcon iconName="attach_money" />
                  </li>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isSmallScreen && (
        <button
          className="CountersToggleButton"
          onClick={() => setIsMobileCountersOpen((prev) => !prev)}
          aria-label={isMobileCountersOpen ? 'Hide charts panel' : 'Show charts panel'}
          title={isMobileCountersOpen ? 'Hide charts panel' : 'Show charts panel'}
        >
          <span className="material-icons">{isMobileCountersOpen ? 'chevron_right' : 'chevron_left'}</span>
        </button>
      )}

      <div className={`Counters ${isSmallScreen ? `CountersMobileDrawer ${isMobileCountersOpen ? 'open' : 'closed'}` : ''}`}>
        <div className="SightseeingCounter">
          <span className="ChartTitle">
            {' '}
            Travel Spots
            <span className="material-icons icon">{'hiking'}</span>
          </span>
          {totalNumSightseeingPosts !== null ? (
            <div className="SightseeingChart">
              <ResponsiveContainer width="100%" height="100%" minWidth={80} minHeight={80}>
                <PieChart margin={{ top: 2, right: 2, left: 2, bottom: 34 }}>
                  <Pie
                    data={pieSightseeingData}
                    cx="50%"
                    cy="42%"
                    innerRadius="64%"
                    outerRadius="92%"
                    paddingAngle={4}
                    dataKey="value"
                    fill="var(--text-dim)"
                  />
                  <text className="InnerCircleNumber" x="50%" y="20%" textAnchor="middle" dominantBaseline="middle" fontSize="30" fill="#333">
                    {totalNumSightseeingPosts}
                  </text>
                  <Tooltip />
                  <Legend verticalAlign="bottom" align="center" iconSize={8} formatter={legendFormatter} wrapperStyle={{ fontSize: '7px', lineHeight: '1.1' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            'Loading...'
          )}
        </div>
        <div className="FoodCounter">
          <span className="ChartTitle">
            {' '}
            Food posts
            <span className="material-icons icon">{'restaurant_menu'}</span>
          </span>
          {totalNumFoodPosts !== null ? (
            <div className="FoodChart">
              <ResponsiveContainer width="100%" height="100%" minWidth={80} minHeight={80}>
                <PieChart margin={{ top: 2, right: 2, left: 2, bottom: 34 }}>
                  <Pie
                    data={pieFoodData}
                    cx="50%"
                    cy="42%"
                    innerRadius="64%"
                    outerRadius="92%"
                    paddingAngle={4}
                    dataKey="value"
                    fill="var(--text-dim)"
                  />
                  <text className="InnerCircleNumber" x="50%" y="20%" textAnchor="middle" dominantBaseline="middle" fontSize="30" fill="#333">
                    {totalNumFoodPosts}
                  </text>
                  <Tooltip />
                  <Legend verticalAlign="bottom" align="center" iconSize={8} formatter={legendFormatter} wrapperStyle={{ fontSize: '7px', lineHeight: '1.1' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            'Loading...'
          )}
        </div>
      </div>
    </div>
  );
}

export default WorldMap;
